import React, { useCallback } from "react";

import { useTranslation } from "react-i18next";

import AvailableItemList from "./AvailableItemList";
import DroppableZone from "./DroppableZone";

import type { JSX } from "react";

import type {
  FooterComponentType,
  HeaderComponentType,
  HeaderFooterComponentParams,
} from "../../../../api/ProjectAPI.types";
import type { ItemDragType } from "../../../../constants/dragTypes";

export type ZoneDisposition = "available" | "left" | "center" | "right";

export interface ItemDisposition {
  id: HeaderComponentType | FooterComponentType;
  label: string;
  icon: JSX.Element;
  rank: number;
  onClickSettings?: () => void;
  componentUuid?: string;
  params?: HeaderFooterComponentParams;
}

export type DispositionState = Record<ZoneDisposition, ItemDisposition[]>;

export const cloneDispositionState = (
  dispositionState: DispositionState,
): DispositionState => ({
  available: dispositionState.available.map((item) => ({ ...item })),
  left: dispositionState.left.map((item) => ({ ...item })),
  center: dispositionState.center.map((item) => ({ ...item })),
  right: dispositionState.right.map((item) => ({ ...item })),
});

interface DispositionProps {
  dispositionState: DispositionState;
  setDispositionState: React.Dispatch<React.SetStateAction<DispositionState>>;
  acceptItemType: ItemDragType;
  zoneList?: Exclude<ZoneDisposition, "available">[];
}

function Disposition({
  dispositionState,
  setDispositionState,
  acceptItemType,
  zoneList = ["left", "center", "right"],
}: DispositionProps): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const handleDropItem = useCallback(
    ({
      itemId,
      targetZone,
    }: {
      itemId: string;
      targetZone: ZoneDisposition;
    }): void => {
      const prevItemList = { ...dispositionState };

      const sourceZone = (Object.keys(prevItemList) as ZoneDisposition[]).find(
        (key) => prevItemList[key].some((item) => item.id === itemId),
      );

      if (!sourceZone || sourceZone === targetZone) return;

      const itemToMove = prevItemList[sourceZone].find(
        (item) => item.id === itemId,
      );

      if (!itemToMove) return;

      const updatedItemToMove = {
        ...itemToMove,
        rank: prevItemList[targetZone].length + 1,
      };

      const deletedRank = itemToMove.rank;

      const updatedSourceZoneItemList = prevItemList[sourceZone].reduce(
        (acc: ItemDisposition[], item) => {
          if (item.id === itemId) {
            return acc;
          }

          const isAfterDeletedRank = item.rank > deletedRank;

          if (isAfterDeletedRank) {
            return [
              ...acc,
              {
                ...item,
                rank: item.rank - 1,
              },
            ];
          }

          return [
            ...acc,
            {
              ...item,
            },
          ];
        },
        [],
      );

      const updatedItemList = {
        ...prevItemList,
        [sourceZone]: updatedSourceZoneItemList,
        [targetZone]: [...prevItemList[targetZone], updatedItemToMove],
      };

      setDispositionState(updatedItemList);
    },
    [dispositionState, setDispositionState],
  );

  const handleMoveItem = useCallback(
    ({
      itemId,
      newRank,
      targetZone,
    }: {
      itemId: string;
      newRank: number;
      targetZone: ZoneDisposition;
    }): void => {
      const prevItemList = { ...dispositionState };
      const prevTargetZoneItemList = prevItemList[targetZone];
      const dragItem = prevTargetZoneItemList.find(
        (item) => item.id === itemId,
      );

      if (!dragItem) return;

      const oldRank = dragItem.rank;

      const updatedList = prevTargetZoneItemList.map((item) => {
        // move all itemList between old and new index
        const isAfterOldAndBeforeNewIndex =
          item.rank > oldRank && item.rank <= newRank;
        const isAfterNewAndBeforeOldIndex =
          item.rank < oldRank && item.rank >= newRank;

        if (isAfterOldAndBeforeNewIndex) {
          return {
            ...item,
            rank: item.rank - 1,
          };
        }

        if (isAfterNewAndBeforeOldIndex) {
          return {
            ...item,
            rank: item.rank + 1,
          };
        }

        // move the item from old to new index
        if (item.rank === oldRank) {
          return {
            ...item,
            rank: newRank,
          };
        }

        return item;
      });

      setDispositionState({
        ...prevItemList,
        [targetZone]: updatedList,
      });
    },
    [dispositionState, setDispositionState],
  );

  const hasLeftZone = zoneList.includes("left");
  const hasCenterZone = zoneList.includes("center");
  const hasRightZone = zoneList.includes("right");

  return (
    <div className="flex flex-col gap-[1.25rem]">
      <div className="flex gap-[0.46875rem]">
        {hasLeftZone ? (
          <DroppableZone
            acceptItemType={acceptItemType}
            itemList={dispositionState.left}
            onDrop={handleDropItem}
            onMove={handleMoveItem}
            title={t("left")}
            zone="left"
          />
        ) : null}
        {hasCenterZone ? (
          <DroppableZone
            acceptItemType={acceptItemType}
            itemList={dispositionState.center}
            onDrop={handleDropItem}
            onMove={handleMoveItem}
            title={t("center")}
            zone="center"
          />
        ) : null}
        {hasRightZone ? (
          <DroppableZone
            acceptItemType={acceptItemType}
            itemList={dispositionState.right}
            onDrop={handleDropItem}
            onMove={handleMoveItem}
            title={t("right")}
            zone="right"
          />
        ) : null}
      </div>
      <AvailableItemList
        acceptItemType={acceptItemType}
        itemList={dispositionState.available}
        onDrop={handleDropItem}
      />
    </div>
  );
}

export default Disposition;
